/**
 * The inspector palette of the version editor's playEngine panel.
 *
 * Those tokens are declared privately inside containerPlayEngineVersionSettings
 * (and repeated in containerPlayEngineAssets with an older, lighter grey pair).
 * The snapshot editor's own panels sit right next to that one in the same tab
 * rail, so they share the palette from here rather than each picking their own
 * greys — three surfaces and one label colour, nothing else.
 */

/** Panel surface — the background everything else sits on. */
export const PANEL_BG = "#2b2b2e";

/** Title / toolbar bars, and any card that should read as raised off the panel. */
export const BAR_BG = "#3a3a41";

/** Hairline between sections and around cards. */
export const SECTION_DIVIDER = "#3a3a3f";

/** Property label text — the one non-surface token, kept here for the same reason. */
export const FIELD_LABEL = "#b8bcc4";

/**
 * Control surface: inputs and buttons sit DARKER than the bar they are on, the
 * way the version editor's language selector does. An outlined button whose
 * border is the divider colour disappears against BAR_BG — the recess is what
 * makes it read as something you can press.
 */
export const CONTROL_BG = "#212226";

/** Edge of a control. Lighter than the divider so a button has a visible rim. */
export const CONTROL_BORDER = "#55585f";

/**
 * Dark scrollbars, for any panel list that scrolls.
 *
 * The browser's default bar is light and cuts a white stripe through these
 * panels. Applied by putting `className="proto-dark-scroll"` on the scrolling
 * element and rendering `<style>{DARK_SCROLLBAR_CSS}</style>` once nearby.
 */
export const DARK_SCROLLBAR_CSS = `
.proto-dark-scroll { scrollbar-width: thin; scrollbar-color: ${CONTROL_BORDER} ${PANEL_BG}; }
.proto-dark-scroll::-webkit-scrollbar { width: 10px; height: 10px; }
.proto-dark-scroll::-webkit-scrollbar-track { background: ${PANEL_BG}; }
.proto-dark-scroll::-webkit-scrollbar-thumb {
  background: ${CONTROL_BORDER};
  border: 2px solid ${PANEL_BG};
  border-radius: 5px;
}
.proto-dark-scroll::-webkit-scrollbar-thumb:hover { background: #6d717a; }
.proto-dark-scroll::-webkit-scrollbar-corner { background: ${PANEL_BG}; }
`;

/**
 * The properties-panel control skin: recessed inputs with a hairline rim, the
 * compact metrics an inspector needs, and no Material underline.
 *
 * Applied by putting `className="proto-props-panel"` on the scrolling panel and
 * rendering `<style>{PROPS_PANEL_CSS}</style>` inside it. It lives here because
 * more than one panel wears it — containerPlayEngineVersionSettings still
 * carries its own identical literal, which can be swapped for this constant
 * whenever that file is next touched.
 */
export const PROPS_PANEL_CSS = `
          .proto-props-panel .MuiInputBase-root {
            background-color: #212226;
            border: 1px solid #3a3a3f;
            border-radius: 4px;
            min-height: 24px;
          }
          .proto-props-panel .MuiInputBase-root.Mui-focused {
            border-color: #64b5f6;
          }
          .proto-props-panel .MuiInputBase-root.Mui-disabled {
            background-color: #1c1d20;
            border-color: #2f3033;
          }
          .proto-props-panel .MuiInputBase-input {
            padding: 3px 6px;
            font-size: 12px;
          }
          .proto-props-panel .MuiInput-underline:before,
          .proto-props-panel .MuiInput-underline:after {
            display: none;
          }
          .proto-props-panel .MuiSelect-select {
            padding: 3px 24px 3px 6px;
            background: transparent;
          }
          .proto-props-panel .MuiSelect-icon {
            color: #8a8d93;
          }
        `;
